import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { formatCompactNumber, formatCurrency, formatNumber } from '../utils/formatters';

export default function KPICard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  icon: Icon,
  format = 'number',
  loading = false,
}) {
  const formattedValue = () => {
    if (loading) return '-';
    switch (format) {
      case 'currency':
        return formatCurrency(value);
      case 'compact':
        return formatCompactNumber(value);
      case 'percentage':
        return `${formatNumber(value, 1)}%`;
      default:
        return formatNumber(value);
    }
  };

  const getTrendIcon = () => {
    if (change > 0) return <TrendingUp className="w-4 h-4" />;
    if (change < 0) return <TrendingDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getTrendColor = () => {
    if (change > 0) return 'text-green-600 bg-green-50';
    if (change < 0) return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="kpi-card">
      {/* Desktop layout: title+value left, icon top-right */}
      <div className="hidden sm:flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className={`mt-2 text-3xl font-bold text-gray-900 ${loading ? 'animate-pulse' : ''}`}>
            {formattedValue()}
          </p>
        </div>
        {Icon && (
          <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-primary-600" />
          </div>
        )}
      </div>

      {/* Mobile layout: number+icon row, then full-width title below */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-lg font-bold text-gray-900 ${loading ? 'animate-pulse' : ''}`}>
            {formattedValue()}
          </p>
          {Icon && (
            <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-primary-600" />
            </div>
          )}
        </div>
        <p className="text-[10px] font-medium text-gray-500 mt-1">{title}</p>
      </div>

      {change !== undefined && (
        <div className="mt-4 flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTrendColor()}`}>
            {getTrendIcon()}
            {Math.abs(change).toFixed(1)}%
          </span>
          <span className="text-xs text-gray-500">{changeLabel}</span>
        </div>
      )}
    </div>
  );
}
